import { VERDICTS } from '../constants/auditResults';
import { CHECKOUT_AUDIT_FAILURE_REASONS, CHECKOUT_AUDIT_STATUSES } from '../constants/checkoutAuditStatuses';

/** @typedef {import('../constants/checkoutAuditStatuses').CheckoutAuditStatus} CheckoutAuditStatus */
/** @typedef {import('../constants/checkoutAuditStatuses').CheckoutAuditFailureReason} CheckoutAuditFailureReason */
/** @typedef {import('../entities/Audit.types').AuditVerdict} AuditVerdict */

const STATUS_BY_VERDICT = {
  [VERDICTS.PASS]: CHECKOUT_AUDIT_STATUSES.PASSED,
  [VERDICTS.FLAG]: CHECKOUT_AUDIT_STATUSES.FLAGGED,
  [VERDICTS.FAIL]: CHECKOUT_AUDIT_STATUSES.FAILED_VERDICT,
};

/**
 * Order matters: a verdict beats an older failed run, an edit makes that verdict stale, and being
 * offline answers only when there is no result to show.
 *
 * @returns {CheckoutAuditStatus}
 */
export function resolveCheckoutAuditStatus({
  isRunning = false,
  verdict = null,
  failureReason = null,
  isNoteChangedSinceAudit = false,
  isOffline = false,
} = {}) {
  if (isRunning) {
    return CHECKOUT_AUDIT_STATUSES.RUNNING;
  }

  const verdictStatus = verdict ? STATUS_BY_VERDICT[verdict] : null;

  if (verdictStatus) {
    return isNoteChangedSinceAudit
      ? CHECKOUT_AUDIT_STATUSES.OUTDATED
      : verdictStatus;
  }

  if (isOffline || failureReason === CHECKOUT_AUDIT_FAILURE_REASONS.UNAVAILABLE) {
    return CHECKOUT_AUDIT_STATUSES.UNAVAILABLE;
  }

  if (failureReason === CHECKOUT_AUDIT_FAILURE_REASONS.ERROR) {
    return CHECKOUT_AUDIT_STATUSES.ERRORED;
  }

  return CHECKOUT_AUDIT_STATUSES.IDLE;
} // resolveCheckoutAuditStatus()

export function isRerunOffered(status) {
  return [
    CHECKOUT_AUDIT_STATUSES.OUTDATED,
    CHECKOUT_AUDIT_STATUSES.ERRORED,
    CHECKOUT_AUDIT_STATUSES.UNAVAILABLE,
  ].includes(status);
} // isRerunOffered()

export function hasAuditResult(status) {
  return [
    CHECKOUT_AUDIT_STATUSES.PASSED,
    CHECKOUT_AUDIT_STATUSES.FLAGGED,
    CHECKOUT_AUDIT_STATUSES.FAILED_VERDICT,
    CHECKOUT_AUDIT_STATUSES.OUTDATED,
  ].includes(status);
} // hasAuditResult()
